import { isNil } from 'lodash';
import { toast } from 'react-toastify';

import { queryClient } from '@/app/lib/tanstack-query';
import { router } from '@/app/lib/tanstack-router';
import {
  AccessTokenResponse,
  accessTokenResponseSchema
} from '@/app/network/models/access-token-response';
import { Toast } from '@/shared/components/toast';

const USER_TOKENS_KEY = 'myTalquin:auth:tokens';
const IMPERSONATION_TOKENS_KEY = 'myTalquin:auth:impersonationTokens';

/**
 * Retrieves and parses the token data stored in the local storage.
 * If the data is not available, invalid, or cannot be parsed, null is returned.
 *
 * @return The token data if successfully retrieved and parsed; otherwise, null.
 */
export function getTokens() {
  const userTokens = getUserTokens();
  const impersonationTokens = getImpersonationTokens();

  return impersonationTokens ?? userTokens;
}

function getUserTokens() {
  try {
    const storageString = localStorage.getItem(USER_TOKENS_KEY);

    if (storageString === null) return null;

    const result = accessTokenResponseSchema.safeParse(JSON.parse(storageString));

    if (!result.success) return null;

    return result.data;
  } catch {
    return null;
  }
}

function getImpersonationTokens() {
  try {
    const storageString = localStorage.getItem(IMPERSONATION_TOKENS_KEY);

    if (storageString === null) return null;

    const result = accessTokenResponseSchema.safeParse(JSON.parse(storageString));

    if (!result.success) return null;

    return result.data;
  } catch {
    return null;
  }
}

/**
 * Stores the provided tokens in local storage.
 *
 * @param {AccessTokenResponse} tokens - The access tokens to be stored.
 * @param isImpersonation - Whether the token is being set in an impersonation capacity.
 */
export function setTokens(tokens: AccessTokenResponse, isImpersonation = false) {
  if (isImpersonation) {
    localStorage.setItem(IMPERSONATION_TOKENS_KEY, JSON.stringify(tokens));
  } else {
    localStorage.setItem(USER_TOKENS_KEY, JSON.stringify(tokens));
  }
}

/**
 * Clears all stored tokens by removing the associated entry in the local storage.
 */
export function clearTokens() {
  localStorage.removeItem(USER_TOKENS_KEY);
  localStorage.removeItem(IMPERSONATION_TOKENS_KEY);
}

/**
 * Checks if there are any tokens available.
 *
 * @return Returns true if tokens exist; otherwise, false.
 */
export function hasTokens() {
  return !isNil(getTokens());
}

export function hasImpersonationTokens() {
  return !isNil(getImpersonationTokens());
}

export async function endUserSession() {
  clearTokens();
  queryClient.clear();

  toast.info(Toast, { data: { title: 'Automatically Signed Out', content: 'You have been signed out, please login to continue.' } });
  await router.navigate({ to: '/login' });
}
